import type { AdminQuestClaim } from '@src/dto/QuestClaims';
import { Store } from '@tanstack/react-store';
import type { SortingState } from '@tanstack/react-table';
import { createManagementActions } from './createManagementStore';

export type ClaimModalMode = 'closed' | 'assign' | 'edit';

interface QuestClaimsState {
  claims: AdminQuestClaim[];
  filters: {
    seasonId: string;
    roundId: string;
    status: string;
    appAndroidPackage: string;
    search: string;
  };
  sorting: SortingState;
  pagination: {
    currentPage: number;
    totalPages: number;
    total: number;
  };
  ui: {
    isLoading: boolean;
    selectedClaimId: string | null;
    modalMode: ClaimModalMode;
  };
}

const initialFilters: QuestClaimsState['filters'] = {
  seasonId: '',
  roundId: '',
  status: 'all',
  appAndroidPackage: '',
  search: '',
};

const defaultSorting: SortingState = [{ id: 'created', desc: true }];

export const questClaimsStore = new Store<QuestClaimsState>({
  claims: [],
  filters: initialFilters,
  sorting: defaultSorting,
  pagination: { currentPage: 1, totalPages: 1, total: 0 },
  ui: {
    isLoading: true,
    selectedClaimId: null,
    modalMode: 'closed',
  },
});

const actions = createManagementActions(questClaimsStore, {
  initialFilters,
  defaultSorting,
});

export const {
  updateFilter,
  setSorting,
  setPage,
  setPaginationInfo,
  setLoading,
  resetFilters,
} = actions;

export const setClaims = (claims: AdminQuestClaim[]) => {
  questClaimsStore.setState((prev) => ({ ...prev, claims }));
};

// Update several filter keys atomically (used by the cascading season → round →
// app selects) so the table only reloads once per change.
export const setFilters = (partial: Partial<QuestClaimsState['filters']>) => {
  questClaimsStore.setState((prev) => ({
    ...prev,
    filters: { ...prev.filters, ...partial },
    pagination: { ...prev.pagination, currentPage: 1 },
  }));
};

export const selectClaim = (claimId: string | null) => {
  actions.setUiField('selectedClaimId', claimId);
};

export const setModalMode = (mode: ClaimModalMode) => {
  actions.setUiField('modalMode', mode);
};

function isSameSorting(a: SortingState, b: SortingState): boolean {
  if (a.length !== b.length) {
    return false;
  }
  return a.every((sort, index) => {
    const other = b[index];
    return (
      other !== undefined && sort.id === other.id && sort.desc === other.desc
    );
  });
}

// Atomically replace the URL-backed slice (filters + sorting + page + the open
// assign/edit modal) from a parsed URL. Returns the previous state untouched
// when nothing differs so the URL→store→URL sync can't loop, and preserves
// nested references so unrelated `useStore` selectors don't re-render.
export const applyQuestClaimsUrlState = (next: {
  filters: QuestClaimsState['filters'];
  sorting: SortingState;
  page: number;
  modalMode: ClaimModalMode;
  selectedClaimId: string | null;
}) => {
  questClaimsStore.setState((prev) => {
    const sameFilters =
      prev.filters.seasonId === next.filters.seasonId &&
      prev.filters.roundId === next.filters.roundId &&
      prev.filters.status === next.filters.status &&
      prev.filters.appAndroidPackage === next.filters.appAndroidPackage &&
      prev.filters.search === next.filters.search;
    const sameSorting = isSameSorting(prev.sorting, next.sorting);
    const samePage = prev.pagination.currentPage === next.page;
    const sameModal =
      prev.ui.modalMode === next.modalMode &&
      prev.ui.selectedClaimId === next.selectedClaimId;

    if (sameFilters && sameSorting && samePage && sameModal) {
      return prev;
    }

    return {
      ...prev,
      filters: sameFilters ? prev.filters : { ...next.filters },
      sorting: sameSorting ? prev.sorting : next.sorting,
      pagination: samePage
        ? prev.pagination
        : { ...prev.pagination, currentPage: next.page },
      ui: sameModal
        ? prev.ui
        : {
            ...prev.ui,
            modalMode: next.modalMode,
            selectedClaimId: next.selectedClaimId,
          },
    };
  });
};
